import { useDispatch } from "react-redux";
import { toggleMenuContent } from "../../../store/themeConfigSlice";
import { STRING_MENU } from "../../../constants/string";
import IconLockDots from "../../../components/icons/icon-lock-dots";
import IconEye from "../../../components/icons/icon-eye";
import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import CookieUtils from "../../../utils/cookie-utils";
import { MODULE_ROUTE } from "../../../routers/module-router";

import { usePageTitle } from "../../../hooks/usePageTitle";
import { EmployeeService } from "../services/employee-service";

const ChangePasswordPage = () => {
  const dispatch = useDispatch();
  dispatch(toggleMenuContent(STRING_MENU.CHANGE_PASSWORD));
  usePageTitle("Đổi mật khẩu");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [reNewPassword, setReNewPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showReNewPassword, setShowReNewPassword] = useState(false);

  const [showErrorOldPassword, setShowErrorOldPassword] = useState(false);
  const [showErrorNewPassword, setShowErrorNewPassword] = useState(false);
  const [showErrorReNewPassword, setShowErrorReNewPassword] = useState(false);

  const [errorOldPassword, setErrorOldPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState("");
  const [errorReNewPassword, setErrorReNewPassword] = useState("");

  const { fetch: onChangePassword, response: responseChangePassword, loading: isLoading } = EmployeeService.changeEmployeePassword();

  useEffect(() => {
    if (!responseChangePassword) {
      return;
    }
    if (responseChangePassword.status === 200) {
      showToast({ message: "Đổi mật khẩu mới thành công", type: "success" });
      CookieUtils.eraseCookie("user");
      CookieUtils.eraseCookie("tokenNotification");
      CookieUtils.eraseCookie("autoLoginQueueWebRTC");

      setTimeout(() => {
        window.location.href = MODULE_ROUTE.LOGIN;
      }, 1000);
    } else {
      showToast({ message: responseChangePassword.message, type: "warning" });
    }
  }, [responseChangePassword]);

  const resetErrors = () => {
    setShowErrorOldPassword(false);
    setShowErrorNewPassword(false);
    setShowErrorReNewPassword(false);
    setErrorOldPassword("");
    setErrorNewPassword("");
    setErrorReNewPassword("");
  };

  const handleChangePassword = () => {
    resetErrors();
    if (!validateNewPassword()) {
      return;
    }
    const userId = CookieUtils.getCurrentUser().id ?? 0;
    onChangePassword(userId.toString(), {
      old_password: btoa(oldPassword),
      new_password: btoa(newPassword),
    });
  };

  const validateNewPassword = () => {
    let isValid = true;
    if (oldPassword.length < 6 || oldPassword.length > 20) {
      setShowErrorOldPassword(true);
      setErrorOldPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword.length < 6 || newPassword.length > 20) {
      setShowErrorNewPassword(true);
      setErrorNewPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword === oldPassword) {
      setShowErrorNewPassword(true);
      setErrorNewPassword("Mật khẩu mới không được giống mật khẩu cũ");
      isValid = false;
    }

    if (reNewPassword.length < 6 || reNewPassword.length > 20) {
      setShowErrorReNewPassword(true);
      setErrorReNewPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword !== reNewPassword) {
      setShowErrorReNewPassword(true);
      setErrorReNewPassword("Mật khẩu nhập lại không giống nhau");
      isValid = false;
    }

    return isValid;
  };

  interface RenderComponentProps {
    labelText: string;
    icon?: React.ReactNode;
    type?: React.HTMLInputTypeAttribute;
    value: string;
    setValue: (value: string) => void;
    isShowError?: boolean;
    messageError?: string;
    showPassword: boolean;
    setShowPassword: (value: boolean) => void;
  }

  function renderComponent({ labelText, type, icon, value, setValue, showPassword, setShowPassword, isShowError, messageError }: RenderComponentProps) {
    return (
      <div>
        <label htmlFor={labelText} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {labelText}
        </label>
        <div className="relative">
          <span className="absolute top-1/2 -translate-y-1/2 start-4 text-white-dark">{icon}</span>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleChangePassword();
              }
            }}
            type={type}
            placeholder="Nhập thông tin"
            aria-invalid={isShowError}
            aria-describedby={isShowError ? `${labelText}-error` : undefined}
            className={`${
              isShowError ? "border-red-500" : "border-gray-300"
            } ps-10 bg-gray-50 border  text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
          />
          <button type="button" className="absolute top-1/2 -translate-y-1/2 end-4" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)}>
            <IconEye isIconOff={showPassword} color="#888ea8" />
          </button>
        </div>
        {isShowError && (
          <span id={`${labelText}-error`} className="text-sm text-red-500">
            {messageError}
          </span>
        )}
      </div>
    );
  }

  return (
    <div>
    
      <div className="relative flex flex-col items-center justify-center h-[calc(100vh-104px)] px-6 py-8 mx-auto bg-center bg-no-repeat bg-cover lg:py-0">
        <div className="w-full rounded-md bg-[linear-gradient(45deg,#fff9f9_0%,rgba(255,255,255,0)_25%,rgba(255,255,255,0)_75%,_#fff9f9_100%)] dark:bg-[linear-gradient(52.22deg,#0E1726_0%,rgba(14,23,38,0)_18.66%,rgba(14,23,38,0)_51.04%,rgba(14,23,38,0)_80.07%,#0E1726_100%)] md:mt-0 sm:max-w-md p-2">
          <div className="w-full rounded-md backdrop-blur-lg bg-white/60 dark:bg-black/50 md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 space-y-4 md:space-y-6">
              <div className="mb-5">
                <h1 className="text-xl uppercase !leading-snug text-primary md:text-4xl" style={{ fontWeight: "800" }}>
                  Đổi mật khẩu
                </h1>
              </div>
              <form
                className="mt-4 space-y-4 lg:mt-5 md:space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleChangePassword();
                }}
                data-testid="form-change-password"
              >
                {renderComponent({
                  labelText: "Mật khẩu hiện tại",
                  type: showOldPassword ? "text" : "password",
                  value: oldPassword,
                  setValue: setOldPassword,
                  showPassword: showOldPassword,
                  setShowPassword: setShowOldPassword,
                  isShowError: showErrorOldPassword,
                  messageError: errorOldPassword,
                  icon: <IconLockDots fill={true} data-testid="old-password-icon" />,
                })}
                {renderComponent({
                  labelText: "Mật khẩu mới",
                  type: showNewPassword ? "text" : "password",
                  value: newPassword,
                  setValue: setNewPassword,
                  showPassword: showNewPassword,
                  setShowPassword: setShowNewPassword,
                  isShowError: showErrorNewPassword,
                  messageError: errorNewPassword,
                  icon: <IconLockDots fill={true} data-testid="new-password-icon" />,
                })}
                {renderComponent({
                  labelText: "Nhập lại mật khẩu",
                  type: showReNewPassword ? "text" : "password",
                  value: reNewPassword,
                  setValue: setReNewPassword,
                  showPassword: showReNewPassword,
                  setShowPassword: setShowReNewPassword,
                  isShowError: showErrorReNewPassword,
                  messageError: errorReNewPassword,
                  icon: <IconLockDots fill={true} data-testid="re-new-password-icon" />,
                })}

                <button
                  type="button"
                  data-testid="button-change-password"
                  className="btn btn-gradient w-full border-0 shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
                  disabled={isLoading}
                  onClick={handleChangePassword}
                >
                  {isLoading && <span className="inline-block w-5 h-5 align-middle rounded-full border-2 border-white animate-spin border-l-transparent ltr:mr-4 rtl:ml-4"></span>}
                  Đổi mật khẩu
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
